import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { Menu } from 'components/Menu';
import { useSelector } from 'hooks/useSelector';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  DELETE_CHOSEN_ASSETS,
  NAVIGATE_MODAL,
  doCloudPropertiesOpen,
  SET_ASSETS_FILTER,
  SET_ASSETS_SORT,
  CLEAR_CHOSEN_ASSETS,
  CHOOSE_ALL_FILTERED_ASSETS,
} from 'store/actions';
import {
  selectAssetListSearchText,
  selectAssetListSort,
  selectAssetsTotal,
  selectChosenAssetsCount,
  selectFilteredAssetsListTotal,
  selectOrganisationFilterValue,
} from 'store/assets/selectors';
import { selectUserFeatureCreateAsset } from 'store/auth/selectors';
import { REMOTE_LOAD_CREATE_ASSET } from 'store/remote/actions';

import { Button } from 'components/Button';
import { PageHeader } from 'components/PageHeader';
import { PageToolbar } from 'components/PageToolbar';
import { AssetsSortOrder } from 'store/assets/types';
import { selectOrganisationFilterOptions } from 'store/organisations/selectors';
import { pluralise } from 'lib/pluralise';
import { Highlight } from 'components/Highlight';
import { ButtonGroup } from 'components/ButtonGroup';
import { useScreenSize } from 'hooks/useScreenSize';
import { ScreenSize } from 'lib/constants';
import { Logo } from 'components/Logo';
import { ModalScreens } from 'components/Navigation/types';
import {
  selectHasSidebarNavigation,
  selectOfflineOnly,
} from 'store/app/selectors';
import { ListSelectAllCheckbox } from 'components/ListSelectAllCheckbox';

type Props = {
  inPages: boolean;
};

const SortValues: { text: string; value: AssetsSortOrder; sort: number }[] = [
  {
    text: 'Name A-Z',
    value: AssetsSortOrder.NAMEASC,
    sort: 1,
  },
  {
    text: 'Name Z-A',
    value: AssetsSortOrder.NAMEDESC,
    sort: 2,
  },
  {
    text: 'Assessment Due',
    value: AssetsSortOrder.ASSESSMENTDUEASC,
    sort: 3,
  },
];

export const LocalAssetsHeader: React.FC<Props> = ({ inPages }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const offlineOnly = useSelector(selectOfflineOnly);
  const hasSidebarNavigation = useSelector(selectHasSidebarNavigation);
  const downloadedPropertiesCount = useSelector(selectAssetsTotal);
  const currentAssetsTotal = useSelector(selectFilteredAssetsListTotal);
  const selectedPropertiesCount = useSelector(selectChosenAssetsCount);
  const canCreateAsset = useSelector(selectUserFeatureCreateAsset);
  const assetsListSearchText = useSelector(selectAssetListSearchText);

  const assetsListSort = useSelector(selectAssetListSort);
  const organisationFilterOptions = useSelector(
    selectOrganisationFilterOptions,
  );
  const organisationFilterValue = useSelector(selectOrganisationFilterValue);

  const openSearch = React.useCallback(
    () => dispatch(doCloudPropertiesOpen()),
    [dispatch],
  );
  const handleSearch = React.useCallback(
    (search: string) => {
      dispatch(SET_ASSETS_FILTER({ search }));
    },
    [dispatch],
  );

  const buttonDownloadAssets = {
    icon: ICONS.ArrowDownLight,
    key: 'download-assets',
    onPress: () => setTimeout(openSearch, 1),
    text: 'Download',
    menuText: 'Download Properties',
  };

  const buttonCreateNewAsset = {
    icon: ICONS.PlusLight,
    key: 'create-new-asset',
    onPress: () => dispatch(REMOTE_LOAD_CREATE_ASSET()),
    text: 'New',
    menuText: 'New Property',
  };

  return (
    <View>
      <PageHeader
        alignContent="left"
        title="Downloaded Properties"
        subtitle={pluralise(
          downloadedPropertiesCount,
          '{x} Property',
          '{x} Properties',
          '',
        )}
        showSidebarButton
        // Get help button (mobile only)
        onPressCloseButton={
          offlineOnly && !hasSidebarNavigation
            ? () =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.Support,
                    params: {},
                  }),
                )
            : undefined
        }
        closeIcon={ICONS.CommentQuestionLight}
        buttonsLeft={
          !inPages && screenSize === ScreenSize.large && <Logo width={100} />
        }
        buttons={
          canCreateAsset ? (
            <ButtonGroup>
              <IconButton
                key={buttonCreateNewAsset.key}
                icon={buttonCreateNewAsset.icon}
                onPress={buttonCreateNewAsset.onPress}
              />
              <IconButton
                key={buttonDownloadAssets.key}
                icon={buttonDownloadAssets.icon}
                onPress={buttonDownloadAssets.onPress}
              />
            </ButtonGroup>
          ) : (
            <ButtonGroup>
              <IconButton
                key={buttonDownloadAssets.key}
                icon={buttonDownloadAssets.icon}
                onPress={buttonDownloadAssets.onPress}
              />
            </ButtonGroup>
          )
        }
      />
      {!!downloadedPropertiesCount && (
        <PageToolbar
          searchDisabled={selectedPropertiesCount > 0}
          searchPlaceholder="Search properties..."
          onSearch={selectedPropertiesCount === 0 && handleSearch}
          searchValue={assetsListSearchText}
          leftChildren={
            <ButtonGroup>
              <ListSelectAllCheckbox
                onSelectAll={() => dispatch(CHOOSE_ALL_FILTERED_ASSETS())}
                onSelectNone={() => dispatch(CLEAR_CHOSEN_ASSETS())}
                selectionState={
                  currentAssetsTotal &&
                  currentAssetsTotal === selectedPropertiesCount
                    ? 'all'
                    : selectedPropertiesCount
                      ? 'some'
                      : 'none'
                }
              />
            </ButtonGroup>
          }
        >
          {selectedPropertiesCount ? (
            <ButtonGroup>
              <Highlight highlight style={{ borderRadius: 99 }}>
                <Button
                  text={pluralise(
                    selectedPropertiesCount,
                    'Remove Property from Device',
                    'Remove {x} Properties from Device',
                  )}
                  onPress={() => dispatch(DELETE_CHOSEN_ASSETS())}
                />
              </Highlight>
            </ButtonGroup>
          ) : (
            <ButtonGroup>
              {screenSize === ScreenSize.large &&
                organisationFilterOptions.length > 1 && (
                  <Menu
                    buttonConfig={{
                      text: organisationFilterValue
                        ? `${organisationFilterOptions.find((v) => v.id === organisationFilterValue)?.name || 'Unknown'}`
                        : `All Organisations`,
                      style: { maxWidth: 150 },
                      showBadge: !!organisationFilterValue,
                    }}
                    width={170}
                    options={[
                      ...organisationFilterOptions.map((v) => ({
                        text: v.name,
                        onPress: () =>
                          dispatch(
                            SET_ASSETS_FILTER({
                              organisations: [v.id],
                            }),
                          ),
                      })),
                      '-',
                      {
                        text: 'All Organisations',
                        onPress: () =>
                          dispatch(
                            SET_ASSETS_FILTER({
                              organisations: [],
                            }),
                          ),
                      },
                    ]}
                  />
                )}
              {screenSize === ScreenSize.large && (
                <Menu
                  buttonConfig={{
                    text:
                      SortValues.find((v) => v.value === assetsListSort)
                        ?.text || 'Order',
                    style: { maxWidth: 150 },
                  }}
                  width={140}
                  options={SortValues.map((v) => ({
                    text: v.text,
                    onPress: () => dispatch(SET_ASSETS_SORT(v.value)),
                  }))}
                />
              )}
              {screenSize !== ScreenSize.large && (
                <IconButton
                  icon={ICONS.SlidersUpLight}
                  onPress={() =>
                    dispatch(
                      NAVIGATE_MODAL({
                        screen: ModalScreens.OfflinePropertyListFilter,
                        size: 'small',
                        params: {},
                      }),
                    )
                  }
                  showBadge={!!organisationFilterValue}
                ></IconButton>
              )}
            </ButtonGroup>
          )}
        </PageToolbar>
      )}
    </View>
  );
};
